import { Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT, SHAPE, TIMING, type ColorToken } from '../config/tokens';
import { frame } from '../assets/loader';
import { sound } from '../audio/sound';

/** Text in the game font. Size/weight/colour from tokens. */
export function label(
  text: string,
  size: number,
  opts: { weight?: string; color?: ColorToken; anchor?: [number, number] } = {},
): Text {
  const t = new Text({
    text,
    style: new TextStyle({
      fontFamily: FONT.family,
      fontSize: size,
      fontWeight: (opts.weight ?? FONT.weight.button) as TextStyle['fontWeight'],
      fill: COLORS[opts.color ?? 'cocoa'],
      lineHeight: size * FONT.lineHeight,
    }),
  });
  t.anchor.set(...(opts.anchor ?? [0.5, 0.5]));
  return t;
}

/** §5 Panel: cream surface, cocoa outline, flat latte lip at the bottom. */
export function roundedPanel(
  w: number,
  h: number,
  opts: { fill?: ColorToken; lip?: ColorToken | null; radius?: number } = {},
): Graphics {
  const r = opts.radius ?? SHAPE.panelRadius;
  const g = new Graphics();
  if (opts.lip !== null) {
    g.roundRect(0, SHAPE.panelLip, w, h, r).fill(COLORS[opts.lip ?? 'latte']).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
  }
  g.roundRect(0, 0, w, h, r).fill(COLORS[opts.fill ?? 'cream']).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
  return g;
}

/** Sprite from the atlas, scaled uniformly so its longer edge is `size`. */
export function spriteFit(name: string, size: number): Sprite {
  const s = new Sprite(frame(name));
  s.scale.set(size / Math.max(s.texture.width, s.texture.height));
  return s;
}

/** Makes a container react to taps with the §13 press feedback (97 %, 100 ms). */
export function pressable(target: Container, onTap: () => void, hitPad = 0): void {
  target.eventMode = 'static';
  target.cursor = 'pointer';
  if (hitPad) {
    const b = target.getLocalBounds();
    target.hitArea = { contains: (x: number, y: number) => x >= b.x - hitPad && x <= b.x + b.width + hitPad && y >= b.y - hitPad && y <= b.y + b.height + hitPad };
  }
  const base = target.scale.x;
  target.on('pointerdown', () => sound.unlock());
  target.on('pointerdown', () => gsap.to(target.scale, { x: base * TIMING.buttonPressScale, y: base * TIMING.buttonPressScale, duration: TIMING.buttonPress / 1000 }));
  const release = () => gsap.to(target.scale, { x: base, y: base, duration: TIMING.buttonPress / 1000 });
  target.on('pointerup', release);
  target.on('pointerupoutside', release);
  target.on('pointertap', () => {
    sound.play('button');
    onTap();
  });
}

export type ButtonVariant = 'primary' | 'secondary' | 'disabled';

/**
 * §5 buttons. Primary: raspberry with cream text and a wood lip; secondary:
 * cream with cocoa text; disabled: latte with wood text, flat, not tappable.
 */
export function button(text: string, w: number, variant: ButtonVariant, onTap: () => void, h = 96): Container {
  const c = new Container();
  const g = new Graphics();
  const r = SHAPE.buttonRadius;
  if (variant !== 'disabled') g.roundRect(-w / 2, -h / 2 + 6, w, h, r).fill(variant === 'primary' ? COLORS.wood : COLORS.latte).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
  const fill = variant === 'primary' ? COLORS.raspberry : variant === 'secondary' ? COLORS.cream : COLORS.latte;
  g.roundRect(-w / 2, -h / 2, w, h, r).fill(fill).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
  const color: ColorToken = variant === 'primary' ? 'cream' : variant === 'secondary' ? 'cocoa' : 'wood';
  c.addChild(g, label(text, h >= 90 ? FONT.size.primaryButton : FONT.size.body, { color }));
  if (variant !== 'disabled') pressable(c, onTap);
  return c;
}

/** Small butter badge with a count, e.g. booster amounts (§7: counts are text, not art). */
export function countBadge(n: number): Container {
  const c = new Container();
  const g = new Graphics().circle(0, 0, 22).fill(COLORS.butter).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
  c.addChild(g, label(String(n), 26, { weight: FONT.weight.heading }));
  return c;
}

export interface BoosterButton {
  view: Container;
  setCount(n: number | null): void;
}

/** Round booster button: sky disc, booster icon, count badge (null = unlimited, no badge). */
export function boosterButton(icon: string, count: number | null, onTap: () => void): BoosterButton {
  const view = new Container();
  const disc = new Graphics().circle(0, 0, 44).fill(COLORS.sky).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
  const art = spriteFit(icon, 76);
  art.anchor.set(0.5);
  const badge = new Container();
  badge.position.set(34, -34);
  view.addChild(disc, art, badge);
  pressable(view, onTap);
  const setCount = (n: number | null) => {
    badge.removeChildren().forEach((c) => c.destroy({ children: true }));
    if (n !== null) badge.addChild(countBadge(n));
    art.alpha = n === 0 ? 0.4 : 1;
  };
  setCount(count);
  return { view, setCount };
}

/** Title pill, e.g. "Your Café", "My Dogs" (mockups 26–30). */
export function titleChip(text: string, minWidth = 300): Container {
  const c = new Container();
  const t = label(text, FONT.size.screenTitle, { weight: FONT.weight.heading });
  const w = Math.max(minWidth, t.width + 80);
  const h = 84;
  const bg = new Graphics()
    .roundRect(-w / 2, -h / 2 + 5, w, h, h / 2).fill(COLORS.latte).stroke({ width: SHAPE.outline, color: COLORS.cocoa })
    .roundRect(-w / 2, -h / 2, w, h, h / 2).fill(COLORS.cream).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
  c.addChild(bg, t);
  return c;
}

export interface CurrencyChip {
  view: Container;
  set(value: number, animate?: boolean): void;
}

/** Coin or star balance: icon + number in a cream pill (§14 CurrencyChip). */
export function currencyChip(icon: string, value: number): CurrencyChip {
  const view = new Container();
  const w = 190;
  const h = 72;
  view.addChild(new Graphics().roundRect(-w / 2, -h / 2, w, h, h / 2).fill(COLORS.cream).stroke({ width: SHAPE.outline, color: COLORS.cocoa }));
  const ic = spriteFit(icon, 60);
  const base = ic.scale.x;
  ic.anchor.set(0.5);
  ic.position.set(-w / 2 + 38, 0);
  const num = label(String(value), FONT.size.body + 4, { weight: FONT.weight.heading, anchor: [0, 0.5] });
  num.position.set(-w / 2 + 78, 2);
  view.addChild(ic, num);
  let shown = value;
  return {
    view,
    set(v: number, animate = true) {
      if (!animate) {
        shown = v;
        num.text = String(v);
        return;
      }
      const from = { n: shown };
      shown = v;
      gsap.to(from, { n: v, duration: TIMING.coinCount / 1000, onUpdate: () => (num.text = String(Math.round(from.n))) });
      gsap.fromTo(ic.scale, { x: base * 1.3, y: base * 1.3 }, { x: base, y: base, duration: 0.35, ease: 'back.out(3)', overwrite: true });
    },
  };
}

export type IconKind = 'back' | 'close' | 'dogs' | 'map' | 'shop' | 'settings' | 'pause';

function drawIcon(kind: IconKind): Container {
  const c = new Container();
  const g = new Graphics();
  const ink = { width: 8, color: COLORS.cocoa, cap: 'round' as const, join: 'round' as const };
  switch (kind) {
    case 'back':
      g.moveTo(8, -16).lineTo(-8, 0).lineTo(8, 16).stroke(ink);
      break;
    case 'close':
      g.moveTo(-13, -13).lineTo(13, 13).moveTo(13, -13).lineTo(-13, 13).stroke(ink);
      break;
    case 'pause':
      g.roundRect(-13, -16, 9, 32, 4).roundRect(4, -16, 9, 32, 4).fill(COLORS.cocoa);
      break;
    case 'dogs': {
      const paw = spriteFit('obstacle_paw_stain', 46);
      paw.anchor.set(0.5);
      paw.tint = COLORS.cocoa;
      c.addChild(paw);
      break;
    }
    case 'map':
      g.poly([-20, -14, -7, -19, 7, -14, 20, -19, 20, 15, 7, 20, -7, 15, -20, 20]).fill(COLORS.sky).stroke({ ...ink, width: 4 });
      g.moveTo(-7, -19).lineTo(-7, 15).moveTo(7, -14).lineTo(7, 20).stroke({ ...ink, width: 3 });
      g.circle(1, -2, 5).fill(COLORS.raspberry);
      break;
    case 'shop': {
      const coin = spriteFit('ui_coin', 46);
      coin.anchor.set(0.5);
      c.addChild(coin);
      break;
    }
    case 'settings':
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        g.circle(Math.cos(a) * 16, Math.sin(a) * 16, 6);
      }
      g.fill(COLORS.cocoa).circle(0, 0, 16).fill(COLORS.cocoa).circle(0, 0, 7).fill(COLORS.cream);
      break;
  }
  c.addChildAt(g, 0);
  return c;
}

/**
 * Round icon button (back, close, navigation). Hit area ≥ 44 px after
 * scaling (§4); optional caption below.
 */
export function iconButton(kind: IconKind, onTap: () => void, caption?: string, size = 84): Container {
  const c = new Container();
  const r = size / 2;
  c.addChild(
    new Graphics().circle(0, 5, r).fill(COLORS.latte).stroke({ width: SHAPE.outline, color: COLORS.cocoa })
      .circle(0, 0, r).fill(COLORS.cream).stroke({ width: SHAPE.outline, color: COLORS.cocoa }),
    drawIcon(kind),
  );
  if (caption) {
    const t = label(caption, 24, { weight: FONT.weight.button });
    t.position.set(0, r + 24);
    c.addChild(t);
  }
  pressable(c, onTap, 10);
  return c;
}

/** Plain text link (e.g. "Give up"), clearly visible and tappable. */
export function textLink(text: string, onTap: () => void): Container {
  const c = new Container();
  const t = label(text, FONT.size.body, { weight: FONT.weight.button });
  const u = new Graphics().rect(-t.width / 2, t.height / 2 - 4, t.width, 3).fill(COLORS.cocoa);
  c.addChild(t, u);
  pressable(c, onTap, 18);
  return c;
}

/** Short note that slides in at the top and disappears ("New! Wooden Hammer"). */
export function toast(parent: Container, text: string, y: number, icon?: string): void {
  const c = new Container();
  const t = label(text, FONT.size.body, { weight: FONT.weight.button, anchor: [0, 0.5] });
  const iconW = icon ? 60 : 0;
  const w = t.width + 64 + iconW;
  c.addChild(new Graphics().roundRect(-w / 2, -40, w, 80, 40).fill(COLORS.cream).stroke({ width: SHAPE.outline, color: COLORS.cocoa }));
  if (icon) {
    const s = spriteFit(icon, 52);
    s.anchor.set(0.5);
    s.position.set(-w / 2 + 32 + 26, 0);
    c.addChild(s);
  }
  t.position.set(-w / 2 + 32 + iconW, 2);
  c.addChild(t);
  c.position.set(512, y - 120);
  c.alpha = 0;
  parent.addChild(c);
  gsap.timeline({ onComplete: () => c.destroy({ children: true }) })
    .to(c, { y, alpha: 1, duration: 0.3, ease: 'back.out(1.6)' })
    .to(c, { alpha: 0, y: y - 40, duration: 0.3 }, '+=2.2');
}

/** setTimeout that is skipped when `owner` was destroyed in the meantime (screen changed). */
export function after(owner: Container, ms: number, fn: () => void): void {
  setTimeout(() => {
    if (!owner.destroyed) fn();
  }, ms);
}
